import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Calendar,
  Check,
  ChevronDown,
  Clock,
  LayoutGrid,
  List as ListIcon,
  Lock,
  LogOut,
  MessageSquare,
  Plus,
  Search,
  Tag,
  Trash2,
  Users,
  X,
} from "lucide-react";
import devBoardService, { devBoardAuth } from "../services/devBoardService";
import UserAvatar from "../components/UserAvatar";

/* --------------------------------------------------------------------------
 * Constants & helpers
 * ----------------------------------------------------------------------- */
const COLUMNS = [
  { key: "backlog", label: "Backlog", dot: "#94a3b8" },
  { key: "in_progress", label: "In Progress", dot: "#3b82f6" },
  { key: "review", label: "Review", dot: "#a855f7" },
  { key: "done", label: "Done", dot: "#22c55e" },
];

const PRIORITIES = {
  low: { label: "Low", color: "#64748b", bg: "rgba(100,116,139,0.15)" },
  medium: { label: "Medium", color: "#0ea5e9", bg: "rgba(14,165,233,0.15)" },
  high: { label: "High", color: "#f59e0b", bg: "rgba(245,158,11,0.16)" },
  urgent: { label: "Urgent", color: "#ef4444", bg: "rgba(239,68,68,0.16)" },
};

const fmtDate = (d) =>
  d
    ? new Date(d + "T00:00:00").toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "";

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

const isOverdue = (d) => d && new Date(d + "T23:59:59") < new Date();

const errText = (e) =>
  e?.response?.data?.detail || e?.message || "Something went wrong";

/* --------------------------------------------------------------------------
 * Small presentational pieces
 * ----------------------------------------------------------------------- */
function Avatar({ member, size = 26 }) {
  if (!member)
    return (
      <div
        className="db-avatar db-avatar--empty"
        style={{ width: size, height: size }}
        title="Unassigned"
      >
        ?
      </div>
    );
  return (
    <div
      className="db-avatar"
      style={{ width: size, height: size, background: member.color }}
      title={member.name}
    >
      <UserAvatar
        src={member.avatar_url}
        name={member.name}
        className="w-full h-full rounded-full object-cover"
      />
    </div>
  );
}

function LabelChip({ label }) {
  return (
    <span className="db-label" style={{ "--c": label.color }}>
      {label.name}
    </span>
  );
}

function PriorityChip({ priority }) {
  const p = PRIORITIES[priority] || PRIORITIES.medium;
  return (
    <span className="db-priority" style={{ color: p.color, background: p.bg }}>
      {p.label}
    </span>
  );
}

/* --------------------------------------------------------------------------
 * Task card
 * ----------------------------------------------------------------------- */
function TaskCard({ task, onOpen, onDragStart }) {
  return (
    <div
      className="db-card"
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      onClick={() => onOpen(task)}
    >
      {task.labels?.length > 0 && (
        <div className="db-card__labels">
          {task.labels.map((l) => (
            <LabelChip key={l.id} label={l} />
          ))}
        </div>
      )}
      <div className="db-card__title">{task.title}</div>
      <div className="db-card__meta">
        <PriorityChip priority={task.priority} />
        {task.due_date && (
          <span
            className={`db-card__due ${isOverdue(task.due_date) && task.status !== "done" ? "is-overdue" : ""}`}
          >
            <Calendar size={12} /> {fmtDate(task.due_date)}
          </span>
        )}
        {task.comment_count > 0 && (
          <span className="db-card__comments">
            <MessageSquare size={12} /> {task.comment_count}
          </span>
        )}
        <span className="db-card__spacer" />
        <Avatar member={task.assignee} />
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Task modal (create + edit + comments + activity)
 * ----------------------------------------------------------------------- */
const EMPTY_TASK = {
  title: "",
  description: "",
  status: "backlog",
  priority: "medium",
  assignee_id: null,
  due_date: null,
  label_ids: [],
};

function TaskModal({ taskId, initialStatus, members, labels, onClose, onSaved }) {
  const isNew = !taskId;
  const [form, setForm] = useState({
    ...EMPTY_TASK,
    status: initialStatus || "backlog",
  });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const t = await devBoardService.getTask(taskId);
      setDetail(t);
      setForm({
        title: t.title,
        description: t.description || "",
        status: t.status,
        priority: t.priority,
        assignee_id: t.assignee?.id || null,
        due_date: t.due_date || null,
        label_ids: t.labels.map((l) => l.id),
      });
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [taskId, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (changes) => setForm((f) => ({ ...f, ...changes }));

  const toggleLabel = (id) =>
    setForm((f) => ({
      ...f,
      label_ids: f.label_ids.includes(id)
        ? f.label_ids.filter((x) => x !== id)
        : [...f.label_ids, id],
    }));

  const save = async () => {
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || null,
        status: form.status,
        priority: form.priority,
        assignee_id: form.assignee_id || null,
        due_date: form.due_date || null,
        label_ids: form.label_ids,
      };
      if (isNew) await devBoardService.createTask(payload);
      else await devBoardService.updateTask(taskId, payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    try {
      await devBoardService.addComment(taskId, { body: comment.trim() });
      setComment("");
      load();
    } catch (e) {
      setError(errText(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this task permanently?")) return;
    try {
      await devBoardService.deleteTask(taskId);
      onSaved();
      onClose();
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <div className="db-modal__backdrop" onClick={onClose}>
      <div
        className="db-modal db-modal--task"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="db-modal__head">
          <span className="db-modal__eyebrow">
            {isNew ? "New task" : "Edit task"}
          </span>
          <button className="db-iconbtn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="db-modal__loading">Loading…</div>
        ) : (
          <div className="db-modal__body">
            <div className="db-modal__main">
              <input
                className="db-input db-input--title"
                placeholder="Task title"
                value={form.title}
                onChange={(e) => patch({ title: e.target.value })}
              />
              <textarea
                className="db-input db-textarea"
                placeholder="Description…"
                rows={5}
                value={form.description}
                onChange={(e) => patch({ description: e.target.value })}
              />

              {!isNew && detail && (
                <>
                  <div className="db-section-title">
                    <MessageSquare size={14} /> Comments
                  </div>
                  <div className="db-comments">
                    {detail.comments.length === 0 && (
                      <div className="db-muted">No comments yet.</div>
                    )}
                    {detail.comments.map((c) => (
                      <div key={c.id} className="db-comment">
                        <div className="db-comment__head">
                          <strong>{c.author}</strong>
                          <span className="db-muted">
                            {fmtDateTime(c.created_at)}
                          </span>
                        </div>
                        <div className="db-comment__body">{c.body}</div>
                      </div>
                    ))}
                  </div>
                  <div className="db-comment-compose">
                    <input
                      className="db-input"
                      placeholder="Write a comment…"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && submitComment()}
                    />
                    <button className="db-btn db-btn--ghost" onClick={submitComment}>
                      Send
                    </button>
                  </div>

                  <div className="db-section-title">
                    <Clock size={14} /> Activity
                  </div>
                  <div className="db-activity">
                    {detail.activity.map((a) => (
                      <div key={a.id} className="db-activity__row">
                        <span className="db-activity__dot" />
                        <span>
                          <strong>{a.actor}</strong> {a.action}
                          {a.detail ? ` — ${a.detail}` : ""}
                        </span>
                        <span className="db-muted">
                          {fmtDateTime(a.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="db-modal__side">
              <label className="db-field">
                <span>Status</span>
                <select
                  className="db-input"
                  value={form.status}
                  onChange={(e) => patch({ status: e.target.value })}
                >
                  {COLUMNS.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="db-field">
                <span>Priority</span>
                <select
                  className="db-input"
                  value={form.priority}
                  onChange={(e) => patch({ priority: e.target.value })}
                >
                  {Object.entries(PRIORITIES).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="db-field">
                <span>Assignee</span>
                <select
                  className="db-input"
                  value={form.assignee_id || ""}
                  onChange={(e) =>
                    patch({ assignee_id: e.target.value || null })
                  }
                >
                  <option value="">Unassigned</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="db-field">
                <span>Due date</span>
                <input
                  type="date"
                  className="db-input"
                  value={form.due_date || ""}
                  onChange={(e) => patch({ due_date: e.target.value || null })}
                />
              </label>

              <div className="db-field">
                <span>Labels</span>
                <div className="db-label-picker">
                  {labels.length === 0 && (
                    <span className="db-muted">No labels yet.</span>
                  )}
                  {labels.map((l) => {
                    const on = form.label_ids.includes(l.id);
                    return (
                      <button
                        key={l.id}
                        className={`db-label db-label--btn ${on ? "is-on" : ""}`}
                        style={{ "--c": l.color }}
                        onClick={() => toggleLabel(l.id)}
                      >
                        {on && <Check size={11} />} {l.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {error && <div className="db-error">{error}</div>}

        <div className="db-modal__foot">
          {!isNew && (
            <button className="db-btn db-btn--danger" onClick={remove}>
              <Trash2 size={15} /> Delete
            </button>
          )}
          <span className="db-card__spacer" />
          <button className="db-btn db-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="db-btn db-btn--primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Create task" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Manage devs / labels
 * ----------------------------------------------------------------------- */
const SWATCHES = [
  "#6366f1", "#0ea5e9", "#22c55e", "#f59e0b", "#ef4444",
  "#a855f7", "#ec4899", "#14b8a6", "#64748b", "#f97316",
];

function ManageModal({ mode, members, labels, onClose, onChanged }) {
  const isDevs = mode === "devs";
  const rows = isDevs ? members : labels;
  const [name, setName] = useState("");
  const [color, setColor] = useState(isDevs ? "#6366f1" : "#64748b");
  const [error, setError] = useState("");

  const add = async () => {
    if (!name.trim()) return;
    try {
      if (isDevs)
        await devBoardService.createMember({ name: name.trim(), color });
      else await devBoardService.createLabel({ name: name.trim(), color });
      setName("");
      onChanged();
    } catch (e) {
      setError(errText(e));
    }
  };

  const remove = async (id) => {
    try {
      if (isDevs) await devBoardService.deleteMember(id);
      else await devBoardService.deleteLabel(id);
      onChanged();
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <div className="db-modal__backdrop" onClick={onClose}>
      <div className="db-modal" onClick={(e) => e.stopPropagation()}>
        <div className="db-modal__head">
          <span className="db-modal__eyebrow">
            {isDevs ? "Manage devs" : "Manage labels"}
          </span>
          <button className="db-iconbtn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="db-manage">
          <div className="db-manage__add">
            <input
              className="db-input"
              placeholder={isDevs ? "Dev name" : "Label name"}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <div className="db-swatches">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  className={`db-swatch ${color === c ? "is-on" : ""}`}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
            <button className="db-btn db-btn--primary" onClick={add}>
              <Plus size={15} /> Add
            </button>
          </div>
          {error && <div className="db-error">{error}</div>}
          <div className="db-manage__list">
            {rows.map((r) => (
              <div key={r.id} className="db-manage__row">
                {isDevs ? (
                  <Avatar member={r} />
                ) : (
                  <span
                    className="db-swatch"
                    style={{ background: r.color, cursor: "default" }}
                  />
                )}
                <span>{r.name}</span>
                <span className="db-card__spacer" />
                <button
                  className="db-iconbtn db-iconbtn--danger"
                  onClick={() => remove(r.id)}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {rows.length === 0 && (
              <div className="db-muted">Nothing here yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Passphrase gate
 * ----------------------------------------------------------------------- */
function Gate({ onAuthed }) {
  const [passphrase, setPassphrase] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { token } = await devBoardService.authenticate(passphrase);
      devBoardAuth.setToken(token);
      onAuthed();
    } catch (err) {
      setError(
        err?.response?.status === 401
          ? "Incorrect passphrase"
          : errText(err)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="db-gate">
      <form className="db-gate__card" onSubmit={submit}>
        <div className="db-gate__badge">
          <Lock size={22} />
        </div>
        <h1>Dev Board</h1>
        <p>Enter the team passphrase to continue.</p>
        <input
          type="password"
          className="db-input"
          placeholder="Passphrase"
          value={passphrase}
          autoFocus
          onChange={(e) => setPassphrase(e.target.value)}
        />
        {error && <div className="db-error">{error}</div>}
        <button className="db-btn db-btn--primary db-btn--block" disabled={busy}>
          {busy ? "Checking…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * "Who are you?" actor picker
 * ----------------------------------------------------------------------- */
function ActorPicker({ members, current, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div className="db-actor" ref={ref}>
      <button className="db-btn db-btn--ghost" onClick={() => setOpen((o) => !o)}>
        {current ? `You: ${current}` : "Who are you?"}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="db-actor__menu">
          {members.map((m) => (
            <button
              key={m.id}
              onClick={() => {
                onPick(m.name);
                setOpen(false);
              }}
            >
              <Avatar member={m} size={20} /> {m.name}
            </button>
          ))}
          {members.length === 0 && (
            <div className="db-muted db-actor__empty">Add devs first.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Main page
 * ----------------------------------------------------------------------- */
export default function DevBoardPage() {
  const [checking, setChecking] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [signedIn, setSignedIn] = useState(devBoardAuth.isSignedIn());

  const [members, setMembers] = useState([]);
  const [labels, setLabels] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [actor, setActorState] = useState(devBoardAuth.getActor());

  const [view, setView] = useState("board");
  const [query, setQuery] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [labelFilter, setLabelFilter] = useState("");

  const [modal, setModal] = useState(null); // {type:'task', taskId, status} | {type:'devs'|'labels'}
  const [error, setError] = useState("");
  const [dragOverCol, setDragOverCol] = useState(null);

  useEffect(() => {
    devBoardService
      .getConfig()
      .then((c) => setEnabled(c.enabled))
      .catch(() => setEnabled(false))
      .finally(() => setChecking(false));
  }, []);

  const refetchMeta = useCallback(async () => {
    try {
      const [m, l] = await Promise.all([
        devBoardService.getMembers(),
        devBoardService.getLabels(),
      ]);
      setMembers(m);
      setLabels(l);
    } catch (e) {
      if (e?.response?.status === 401) {
        devBoardAuth.clearToken();
        setSignedIn(false);
      }
    }
  }, []);

  const refetchTasks = useCallback(async () => {
    try {
      const params = {};
      if (query) params.q = query;
      if (assigneeFilter) params.assignee_id = assigneeFilter;
      if (labelFilter) params.label_id = labelFilter;
      const t = await devBoardService.getTasks(params);
      setTasks(t);
    } catch (e) {
      if (e?.response?.status === 401) {
        devBoardAuth.clearToken();
        setSignedIn(false);
      } else {
        setError(errText(e));
      }
    }
  }, [query, assigneeFilter, labelFilter]);

  useEffect(() => {
    if (signedIn) refetchMeta();
  }, [signedIn, refetchMeta]);

  useEffect(() => {
    if (signedIn) refetchTasks();
  }, [signedIn, refetchTasks]);

  const setActor = (name) => {
    devBoardAuth.setActor(name);
    setActorState(name);
  };

  const signOut = () => {
    devBoardAuth.clearToken();
    setSignedIn(false);
  };

  const onDragStart = (e, task) => {
    e.dataTransfer.setData("text/plain", task.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = async (e, status) => {
    e.preventDefault();
    setDragOverCol(null);
    const id = e.dataTransfer.getData("text/plain");
    const task = tasks.find((t) => t.id === id);
    if (!task || task.status === status) return;
    // Optimistic move.
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status } : t))
    );
    try {
      await devBoardService.updateTask(id, { status });
      refetchTasks();
    } catch (err) {
      setError(errText(err));
      refetchTasks();
    }
  };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    for (const t of tasks) (g[t.status] || g.backlog).push(t);
    return g;
  }, [tasks]);

  const openTask = (task) =>
    setModal({ type: "task", taskId: task.id });
  const newTask = (status) =>
    setModal({ type: "task", taskId: null, status });

  if (checking) {
    return (
      <div className="db-root db-center">
        <div className="db-spinner" />
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="db-root db-center">
        <div className="db-gate__card">
          <div className="db-gate__badge">
            <Lock size={22} />
          </div>
          <h1>Dev Board</h1>
          <p>
            This board isn't enabled. Set <code>DEV_BOARD_PASSWORD</code> in the
            backend environment to turn it on.
          </p>
        </div>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div className="db-root">
        <DevBoardStyles />
        <Gate onAuthed={() => setSignedIn(true)} />
      </div>
    );
  }

  return (
    <div className="db-root">
      <DevBoardStyles />

      <header className="db-header">
        <div className="db-header__brand">
          <div className="db-header__mark">DB</div>
          <div>
            <div className="db-header__title">Dev Board</div>
            <div className="db-header__sub">
              {tasks.length} task{tasks.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        <div className="db-header__actions">
          <div className="db-search">
            <Search size={15} />
            <input
              placeholder="Search tasks…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <select
            className="db-input db-input--sm"
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
          >
            <option value="">All assignees</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <select
            className="db-input db-input--sm"
            value={labelFilter}
            onChange={(e) => setLabelFilter(e.target.value)}
          >
            <option value="">All labels</option>
            {labels.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>

          <div className="db-viewtoggle">
            <button
              className={view === "board" ? "is-on" : ""}
              onClick={() => setView("board")}
              title="Board"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={view === "list" ? "is-on" : ""}
              onClick={() => setView("list")}
              title="List"
            >
              <ListIcon size={16} />
            </button>
          </div>

          <ActorPicker members={members} current={actor} onPick={setActor} />
          <button
            className="db-btn db-btn--ghost"
            onClick={() => setModal({ type: "devs" })}
          >
            <Users size={15} /> Devs
          </button>
          <button
            className="db-btn db-btn--ghost"
            onClick={() => setModal({ type: "labels" })}
          >
            <Tag size={15} /> Labels
          </button>
          <button
            className="db-btn db-btn--primary"
            onClick={() => newTask("backlog")}
          >
            <Plus size={16} /> New task
          </button>
          <button className="db-iconbtn" onClick={signOut} title="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {error && (
        <div className="db-error db-error--banner" onClick={() => setError("")}>
          {error} (click to dismiss)
        </div>
      )}

      {view === "board" ? (
        <div className="db-board">
          {COLUMNS.map((col) => (
            <div
              key={col.key}
              className={`db-col ${dragOverCol === col.key ? "is-over" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverCol(col.key);
              }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={(e) => onDrop(e, col.key)}
            >
              <div className="db-col__head">
                <span className="db-col__dot" style={{ background: col.dot }} />
                <span className="db-col__name">{col.label}</span>
                <span className="db-col__count">{grouped[col.key].length}</span>
                <button
                  className="db-iconbtn db-iconbtn--sm"
                  onClick={() => newTask(col.key)}
                  title="Add task"
                >
                  <Plus size={14} />
                </button>
              </div>
              <div className="db-col__body">
                {grouped[col.key].map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    onOpen={openTask}
                    onDragStart={onDragStart}
                  />
                ))}
                {grouped[col.key].length === 0 && (
                  <div className="db-col__empty">Drop tasks here</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="db-listwrap">
          <table className="db-list">
            <thead>
              <tr>
                <th>Task</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Assignee</th>
                <th>Due</th>
                <th>Labels</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.id} onClick={() => openTask(t)}>
                  <td className="db-list__title">{t.title}</td>
                  <td>
                    <span className="db-status-pill">
                      {COLUMNS.find((c) => c.key === t.status)?.label ||
                        t.status}
                    </span>
                  </td>
                  <td>
                    <PriorityChip priority={t.priority} />
                  </td>
                  <td>
                    <div className="db-list__assignee">
                      <Avatar member={t.assignee} size={22} />
                      <span>{t.assignee?.name || "—"}</span>
                    </div>
                  </td>
                  <td
                    className={
                      isOverdue(t.due_date) && t.status !== "done"
                        ? "is-overdue"
                        : ""
                    }
                  >
                    {fmtDate(t.due_date) || "—"}
                  </td>
                  <td>
                    <div className="db-card__labels">
                      {t.labels.map((l) => (
                        <LabelChip key={l.id} label={l} />
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
              {tasks.length === 0 && (
                <tr>
                  <td colSpan={6} className="db-muted" style={{ padding: 24 }}>
                    No tasks match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal?.type === "task" && (
        <TaskModal
          taskId={modal.taskId}
          initialStatus={modal.status}
          members={members}
          labels={labels}
          onClose={() => setModal(null)}
          onSaved={refetchTasks}
        />
      )}
      {(modal?.type === "devs" || modal?.type === "labels") && (
        <ManageModal
          mode={modal.type}
          members={members}
          labels={labels}
          onClose={() => setModal(null)}
          onChanged={() => {
            refetchMeta();
            refetchTasks();
          }}
        />
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Scoped styles (kept in-file so the board is fully self-contained)
 * ----------------------------------------------------------------------- */
function DevBoardStyles() {
  return (
    <style>{`
.db-root{--bg:#0b1220;--panel:#111a2e;--panel2:#0f1728;--card:#16223b;--line:#22304d;
  --text:#e6edf8;--muted:#8697b6;--accent:#6366f1;--accent2:#8b5cf6;
  position:fixed;inset:0;overflow:auto;background:
   radial-gradient(1200px 600px at 12% -10%,rgba(99,102,241,.16),transparent 60%),
   radial-gradient(1000px 500px at 100% 0%,rgba(139,92,246,.12),transparent 55%),var(--bg);
  color:var(--text);font-family:'Inter',ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif;}
.db-center{display:flex;align-items:center;justify-content:center;}
.db-muted{color:var(--muted);font-size:13px;}
.db-spinner{width:34px;height:34px;border-radius:50%;border:3px solid var(--line);
  border-top-color:var(--accent);animation:dbspin .8s linear infinite;}
@keyframes dbspin{to{transform:rotate(360deg)}}

/* header */
.db-header{position:sticky;top:0;z-index:20;display:flex;align-items:center;justify-content:space-between;
  gap:16px;padding:14px 22px;background:rgba(11,18,32,.78);backdrop-filter:blur(12px);
  border-bottom:1px solid var(--line);flex-wrap:wrap;}
.db-header__brand{display:flex;align-items:center;gap:12px;}
.db-header__mark{width:38px;height:38px;border-radius:11px;display:grid;place-items:center;
  font-weight:800;letter-spacing:.5px;color:#fff;font-size:14px;
  background:linear-gradient(135deg,var(--accent),var(--accent2));box-shadow:0 6px 18px rgba(99,102,241,.35);}
.db-header__title{font-weight:700;font-size:16px;line-height:1.1;}
.db-header__sub{color:var(--muted);font-size:12px;}
.db-header__actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}

.db-search{display:flex;align-items:center;gap:7px;background:var(--panel);border:1px solid var(--line);
  border-radius:10px;padding:0 10px;color:var(--muted);}
.db-search input{background:transparent;border:none;outline:none;color:var(--text);padding:8px 2px;font-size:13px;width:150px;}

.db-input{background:var(--panel);border:1px solid var(--line);border-radius:10px;color:var(--text);
  padding:9px 11px;font-size:13px;outline:none;transition:border-color .15s,box-shadow .15s;width:100%;}
.db-input:focus{border-color:var(--accent);box-shadow:0 0 0 3px rgba(99,102,241,.2);}
.db-input--sm{width:auto;padding:8px 10px;}
.db-input--title{font-size:18px;font-weight:600;padding:12px;}
.db-textarea{resize:vertical;font-family:inherit;line-height:1.5;}
select.db-input{cursor:pointer;}

.db-viewtoggle{display:flex;background:var(--panel);border:1px solid var(--line);border-radius:10px;overflow:hidden;}
.db-viewtoggle button{padding:8px 10px;color:var(--muted);background:transparent;border:none;cursor:pointer;display:grid;place-items:center;}
.db-viewtoggle button.is-on{background:var(--accent);color:#fff;}

/* buttons */
.db-btn{display:inline-flex;align-items:center;gap:6px;border-radius:10px;padding:9px 13px;font-size:13px;
  font-weight:600;cursor:pointer;border:1px solid transparent;transition:transform .06s,background .15s,border-color .15s;white-space:nowrap;}
.db-btn:active{transform:translateY(1px);}
.db-btn--primary{background:linear-gradient(135deg,var(--accent),var(--accent2));color:#fff;box-shadow:0 5px 16px rgba(99,102,241,.3);}
.db-btn--primary:disabled{opacity:.6;cursor:default;}
.db-btn--ghost{background:var(--panel);border-color:var(--line);color:var(--text);}
.db-btn--ghost:hover{border-color:var(--accent);}
.db-btn--danger{background:rgba(239,68,68,.12);color:#f87171;border-color:rgba(239,68,68,.3);}
.db-btn--block{width:100%;justify-content:center;padding:11px;}
.db-iconbtn{display:grid;place-items:center;width:36px;height:36px;border-radius:10px;background:var(--panel);
  border:1px solid var(--line);color:var(--muted);cursor:pointer;transition:color .15s,border-color .15s;}
.db-iconbtn:hover{color:var(--text);border-color:var(--accent);}
.db-iconbtn--sm{width:26px;height:26px;border-color:transparent;background:transparent;}
.db-iconbtn--danger:hover{color:#f87171;border-color:rgba(239,68,68,.4);}

/* board */
.db-board{display:grid;grid-template-columns:repeat(4,minmax(260px,1fr));gap:16px;padding:20px 22px;align-items:start;}
.db-col{background:var(--panel2);border:1px solid var(--line);border-radius:16px;padding:12px;min-height:120px;transition:border-color .15s,background .15s;}
.db-col.is-over{border-color:var(--accent);background:rgba(99,102,241,.08);}
.db-col__head{display:flex;align-items:center;gap:8px;padding:4px 6px 12px;}
.db-col__dot{width:9px;height:9px;border-radius:50%;}
.db-col__name{font-weight:600;font-size:13px;}
.db-col__count{color:var(--muted);font-size:12px;background:var(--panel);border:1px solid var(--line);border-radius:20px;padding:1px 8px;}
.db-col__head .db-card__spacer{flex:1;}
.db-col__body{display:flex;flex-direction:column;gap:10px;min-height:40px;}
.db-col__empty{border:1px dashed var(--line);border-radius:12px;padding:18px;text-align:center;color:var(--muted);font-size:12px;}

/* card */
.db-card{background:var(--card);border:1px solid var(--line);border-radius:13px;padding:12px;cursor:pointer;
  transition:transform .08s,border-color .15s,box-shadow .15s;}
.db-card:hover{border-color:var(--accent);box-shadow:0 8px 22px rgba(0,0,0,.35);transform:translateY(-1px);}
.db-card__labels{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:8px;}
.db-card__title{font-size:14px;font-weight:550;line-height:1.35;margin-bottom:10px;}
.db-card__meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.db-card__spacer{flex:1;}
.db-card__due,.db-card__comments{display:inline-flex;align-items:center;gap:4px;color:var(--muted);font-size:12px;}
.db-card__due.is-overdue,.is-overdue{color:#f87171;}

.db-label{display:inline-flex;align-items:center;gap:3px;font-size:11px;font-weight:600;padding:2px 8px;border-radius:20px;
  color:var(--c);background:color-mix(in srgb,var(--c) 18%,transparent);border:1px solid color-mix(in srgb,var(--c) 35%,transparent);}
.db-label--btn{cursor:pointer;background:transparent;}
.db-label--btn.is-on{background:color-mix(in srgb,var(--c) 22%,transparent);}
.db-priority{font-size:11px;font-weight:700;padding:2px 8px;border-radius:6px;}

/* avatar */
.db-avatar{border-radius:50%;display:grid;place-items:center;font-size:10px;font-weight:700;color:#fff;overflow:hidden;flex:none;
  box-shadow:0 0 0 2px rgba(255,255,255,.08);}
.db-avatar img{width:100%;height:100%;object-fit:cover;}
.db-avatar--empty{background:var(--panel);color:var(--muted);box-shadow:inset 0 0 0 1px var(--line);}

/* list */
.db-listwrap{padding:20px 22px;overflow-x:auto;}
.db-list{width:100%;border-collapse:separate;border-spacing:0;background:var(--panel2);border:1px solid var(--line);border-radius:14px;overflow:hidden;}
.db-list th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.6px;color:var(--muted);padding:12px 14px;border-bottom:1px solid var(--line);}
.db-list td{padding:12px 14px;border-bottom:1px solid var(--line);font-size:13px;}
.db-list tbody tr{cursor:pointer;transition:background .12s;}
.db-list tbody tr:hover{background:rgba(99,102,241,.07);}
.db-list__title{font-weight:600;}
.db-list__assignee{display:flex;align-items:center;gap:8px;}
.db-status-pill{font-size:12px;background:var(--panel);border:1px solid var(--line);border-radius:20px;padding:3px 10px;}

/* modal */
.db-modal__backdrop{position:fixed;inset:0;background:rgba(3,7,15,.66);backdrop-filter:blur(3px);z-index:60;
  display:flex;align-items:flex-start;justify-content:center;padding:40px 16px;overflow:auto;}
.db-modal{width:100%;max-width:520px;background:var(--panel);border:1px solid var(--line);border-radius:18px;
  box-shadow:0 30px 80px rgba(0,0,0,.5);overflow:hidden;animation:dbpop .16s ease-out;}
.db-modal--task{max-width:860px;}
@keyframes dbpop{from{opacity:0;transform:translateY(8px) scale(.99)}to{opacity:1;transform:none}}
.db-modal__head{display:flex;align-items:center;justify-content:space-between;padding:16px 18px;border-bottom:1px solid var(--line);}
.db-modal__eyebrow{font-size:12px;text-transform:uppercase;letter-spacing:.7px;color:var(--muted);font-weight:700;}
.db-modal__loading{padding:40px;text-align:center;color:var(--muted);}
.db-modal__body{display:grid;grid-template-columns:1fr 260px;gap:0;}
.db-modal__main{padding:18px;display:flex;flex-direction:column;gap:12px;border-right:1px solid var(--line);min-width:0;}
.db-modal__side{padding:18px;display:flex;flex-direction:column;gap:14px;background:var(--panel2);}
.db-modal__foot{display:flex;align-items:center;gap:10px;padding:14px 18px;border-top:1px solid var(--line);}
.db-field{display:flex;flex-direction:column;gap:6px;font-size:12px;color:var(--muted);font-weight:600;}
.db-field>span{text-transform:uppercase;letter-spacing:.5px;font-size:11px;}
.db-label-picker{display:flex;flex-wrap:wrap;gap:6px;}
.db-section-title{display:flex;align-items:center;gap:7px;font-size:12px;font-weight:700;text-transform:uppercase;
  letter-spacing:.5px;color:var(--muted);margin-top:6px;}
.db-comments{display:flex;flex-direction:column;gap:10px;max-height:220px;overflow:auto;}
.db-comment{background:var(--panel2);border:1px solid var(--line);border-radius:10px;padding:10px 12px;}
.db-comment__head{display:flex;justify-content:space-between;gap:10px;font-size:12px;margin-bottom:4px;}
.db-comment__body{font-size:13px;line-height:1.45;white-space:pre-wrap;}
.db-comment-compose{display:flex;gap:8px;}
.db-activity{display:flex;flex-direction:column;gap:8px;max-height:180px;overflow:auto;}
.db-activity__row{display:flex;align-items:center;gap:8px;font-size:12px;}
.db-activity__row>span:nth-child(2){flex:1;}
.db-activity__dot{width:7px;height:7px;border-radius:50%;background:var(--accent);flex:none;}

/* manage */
.db-manage{padding:18px;display:flex;flex-direction:column;gap:14px;}
.db-manage__add{display:flex;flex-direction:column;gap:10px;}
.db-swatches{display:flex;gap:6px;flex-wrap:wrap;}
.db-swatch{width:24px;height:24px;border-radius:7px;border:2px solid transparent;cursor:pointer;}
.db-swatch.is-on{border-color:#fff;box-shadow:0 0 0 2px var(--accent);}
.db-manage__list{display:flex;flex-direction:column;gap:8px;max-height:340px;overflow:auto;}
.db-manage__row{display:flex;align-items:center;gap:10px;background:var(--panel2);border:1px solid var(--line);border-radius:10px;padding:9px 12px;font-size:13px;}

/* gate */
.db-gate{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:20px;}
.db-gate__card{width:100%;max-width:360px;background:var(--panel);border:1px solid var(--line);border-radius:20px;
  padding:32px;text-align:center;box-shadow:0 30px 80px rgba(0,0,0,.5);display:flex;flex-direction:column;gap:12px;}
.db-gate__card h1{font-size:22px;font-weight:750;}
.db-gate__card p{color:var(--muted);font-size:13px;line-height:1.5;margin-bottom:4px;}
.db-gate__card code{background:var(--panel2);border:1px solid var(--line);padding:1px 6px;border-radius:6px;font-size:12px;}
.db-gate__badge{width:52px;height:52px;border-radius:15px;margin:0 auto;display:grid;place-items:center;color:#fff;
  background:linear-gradient(135deg,var(--accent),var(--accent2));box-shadow:0 10px 26px rgba(99,102,241,.4);}

/* actor picker */
.db-actor{position:relative;}
.db-actor__menu{position:absolute;top:calc(100% + 6px);right:0;min-width:180px;background:var(--panel);
  border:1px solid var(--line);border-radius:12px;box-shadow:0 20px 50px rgba(0,0,0,.5);padding:6px;z-index:30;max-height:280px;overflow:auto;}
.db-actor__menu button{display:flex;align-items:center;gap:9px;width:100%;padding:8px 10px;border-radius:8px;
  background:transparent;border:none;color:var(--text);font-size:13px;cursor:pointer;text-align:left;}
.db-actor__menu button:hover{background:var(--panel2);}
.db-actor__empty{padding:10px;}

/* errors */
.db-error{color:#fca5a5;background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.3);border-radius:10px;
  padding:9px 12px;font-size:13px;}
.db-error--banner{margin:0 22px;cursor:pointer;}

@media (max-width:900px){
  .db-board{grid-template-columns:1fr;}
  .db-modal__body{grid-template-columns:1fr;}
  .db-modal__main{border-right:none;border-bottom:1px solid var(--line);}
}
`}</style>
  );
}
